import axios from "axios";

function truthyEnv(value) {
  return ["1", "true", "yes", "on"].includes(String(value || "").trim().toLowerCase());
}

function resolveProjectId() {
  return process.env.REACT_APP_FB_PROJECT_ID || "speak-your-menu";
}

function resolveFunctionsRegion() {
  return process.env.REACT_APP_FUNCTIONS_REGION || "us-central1";
}

function shouldUseFunctionsEmulator() {
  return truthyEnv(process.env.REACT_APP_USE_FIREBASE_EMULATORS);
}

function isLocalhost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
}

function resolveDeployedFunctionUrl(functionName) {
  const projectId = resolveProjectId();
  const region = resolveFunctionsRegion();
  return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
}

function resolveLocalFunctionUrl(functionName) {
  const projectId = resolveProjectId();
  const region = resolveFunctionsRegion();
  return `http://127.0.0.1:5001/${projectId}/${region}/${functionName}`;
}

// Prefer env-configured RTDB URL, fallback to previous hardcoded URL
const RAW_BASE =
  process.env.REACT_APP_FB_DATABASE_URL ||
  "https://speak-your-menu-default-rtdb.firebaseio.com/";
const BACKEND_URL = RAW_BASE.endsWith("/") ? RAW_BASE : RAW_BASE + "/";

function authQueryParams(idToken, extraParams) {
  return {
    ...(extraParams || {}),
    ...(idToken ? { auth: idToken } : {}),
  };
}

function normalizePartnerRestaurant(raw, index = 0) {
  const entry = raw && typeof raw === "object" ? raw : {};
  const files = entry.files && typeof entry.files === "object" ? entry.files : {};

  return {
    restaurantIndex:
      Number.isInteger(entry.restaurantIndex) && entry.restaurantIndex >= 0
        ? entry.restaurantIndex
        : index,
    restaurantName: String(entry.restaurantName || "").trim(),
    restaurantAddress: String(entry.restaurantAddress || "").trim(),
    cuisineType: String(entry.cuisineType || "").trim(),
    files: {
      foodMenuFiles: Array.isArray(files.foodMenuFiles) ? files.foodMenuFiles : [],
      wineMenuFiles: Array.isArray(files.wineMenuFiles) ? files.wineMenuFiles : [],
      employeeRosterFiles: Array.isArray(files.employeeRosterFiles) ? files.employeeRosterFiles : [],
      // Legacy singular fields — kept for backward compat with old records
      foodMenuFile: files.foodMenuFile || null,
      wineMenuFile: files.wineMenuFile || null,
      employeeRosterFile: files.employeeRosterFile || null,
      supportingDocuments: Array.isArray(files.supportingDocuments)
        ? files.supportingDocuments
        : [],
    },
  };
}

function normalizeLegacyPartnerRestaurants(item) {
  const entry = item && typeof item === "object" ? item : {};
  const hasLegacyFields = Boolean(
    entry.restaurantName ||
      entry.restaurantAddress ||
      entry.cuisineType ||
      entry.files
  );

  if (!hasLegacyFields) return [];

  return [
    normalizePartnerRestaurant(
      {
        restaurantIndex: 0,
        restaurantName: entry.restaurantName,
        restaurantAddress: entry.restaurantAddress,
        cuisineType: entry.cuisineType,
        files: entry.files,
      },
      0
    ),
  ];
}

function normalizePartnerIntakeRecord(id, raw) {
  const entry = raw && typeof raw === "object" ? raw : {};
  const restaurants = Array.isArray(entry.restaurants)
    ? entry.restaurants.map((restaurant, index) => normalizePartnerRestaurant(restaurant, index))
    : normalizeLegacyPartnerRestaurants(entry);
  const restaurantSummary = restaurants
    .map((restaurant) => restaurant.restaurantName)
    .filter(Boolean)
    .join(", ");

  return {
    id,
    ...entry,
    partnerId: String(entry.partnerId || "").trim(),
    contactName: String(entry.contactName || "").trim(),
    contactEmail: String(entry.contactEmail || "").trim().toLowerCase(),
    contactPhone: String(entry.contactPhone || "").trim(),
    restaurantCount:
      Number.isInteger(entry.restaurantCount) && entry.restaurantCount > 0
        ? entry.restaurantCount
        : restaurants.length,
    restaurants,
    restaurantSummary,
    submissionEmailStatus: String(entry.submissionEmailStatus || "").trim(),
    submissionEmailError: String(entry.submissionEmailError || "").trim(),
    completionEmailStatus: String(entry.completionEmailStatus || "").trim(),
    completionEmailError: String(entry.completionEmailError || "").trim(),
    createdAt: typeof entry.createdAt === "number" ? entry.createdAt : 0,
    completionEmailSentAt:
      typeof entry.completionEmailSentAt === "number" ? entry.completionEmailSentAt : null,
  };
}

  // USER CRUD FUNCTIONS

  /**
   * Store a new user in Firebase Realtime Database.
   * @param {Object} userData - The user data to store.
   * @returns {Promise<void>}
   */
  export async function storeUser(userData) {
    // Ensure every signup has a createdAt timestamp
    const payload = {
      ...userData,
      createdAt: Date.now(),
    };
    const response = await axios.post(BACKEND_URL + "users.json", payload);
    console.log("🚀 ~ storeUser ~ response:", response);
    return response.data?.name;
  }

function resolvePlaybookSubmitUrl() {
  const direct = process.env.REACT_APP_SUBMIT_PLAYBOOK_URL;
  if (direct) return direct.replace(/\/$/, "");

  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      return resolveLocalFunctionUrl("submitPlaybookLead");
    }
    return resolveDeployedFunctionUrl("submitPlaybookLead");
  }

  return "/api/submitPlaybookLead";
}

function resolvePlaybookLeadsUrl() {
  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      return resolveLocalFunctionUrl("getPlaybookLeads");
    }
    return resolveDeployedFunctionUrl("getPlaybookLeads");
  }

  const direct = process.env.REACT_APP_GET_PLAYBOOK_LEADS_URL;
  if (direct) return direct.replace(/\/$/, "");

  return "/api/getPlaybookLeads";
}

  export async function storePlaybookLead(playbookData) {
    const payload = {
      ...playbookData,
      createdAt: Date.now(),
    };
    const response = await axios.post(resolvePlaybookSubmitUrl(), payload);
    return response.data;
  }

  /**
   * Retrieve all users from Firebase Realtime Database.
   * @returns {Promise<Array>} Array of user objects, each with an 'id' field.
   */
  export async function getUsers(idToken) {
    const response = await axios.get(BACKEND_URL + "users.json", {
      params: authQueryParams(idToken),
    });
    
    const usersData = [];

    // console.log("🚀 ~ fetchExpenses ~ response.data:", response.data)
    for (const [id, entry] of Object.entries(response.data || {})) {
      const userObj = {
        id,
        fullname: entry.fullname,
        email: entry.email,
        role: entry.role,
        restaurant: entry.restaurant,
        city: entry.city,
        state: entry.state,
        zip: entry.zip,
        createdAt: entry.createdAt,
        source: entry.source,
        emailStatus: entry.emailStatus,
        emailSentAt: entry.emailSentAt,
        emailError: entry.emailError,

      };
      usersData.push(userObj);
    }

    usersData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  
    return usersData;
  }

// BLOG CRUD FUNCTIONS

/**
 * Create a new blog post in Firebase Realtime Database.
 * @param {Object} blogData - The blog post data to store.
 * @returns {Promise<string>} The new blog post ID (Firebase key).
 */
export async function storeBlog(blogData, idToken) {
  const response = await axios.post(BACKEND_URL + "blogs.json", blogData, {
    params: authQueryParams(idToken),
  });
  return response.data?.name;
}

/**
 * Retrieve all blog posts from Firebase Realtime Database.
 * @returns {Promise<Array>} Array of blog post objects, each with an 'id' field.
 */
export async function getBlogs(idToken) {
  const response = idToken
    ? await axios.get(BACKEND_URL + "blogs.json", {
        params: authQueryParams(idToken),
      })
    : await axios.get(BACKEND_URL + "blogs.json");
  const blogsData = [];
  const rawItems = response.data;
  if (rawItems) {
    if (Array.isArray(rawItems)) {
      for (const item of rawItems) {
        blogsData.push(item);
      }
    } else {
      for (const key in rawItems) {
        blogsData.push({ id: key, ...rawItems[key] });
      }
    }
  }
  return blogsData;
}

/**
 * Update an existing blog post in Firebase Realtime Database.
 * @param {string} blogId - The Firebase key of the blog post to update.
 * @param {Object} blogData - The updated blog post data.
 * @returns {Promise<Object>} The response from Firebase.
 */
export async function updateBlog(blogId, blogData, idToken) {
  const response = await axios.patch(
    BACKEND_URL + `blogs/${blogId}.json`,
    blogData,
    { params: authQueryParams(idToken) }
  );
  return response.data;
}

/**
 * Delete a blog post from Firebase Realtime Database.
 * @param {string} blogId - The Firebase key of the blog post to delete.
 * @returns {Promise<Object>} The response from Firebase.
 */
export async function deleteBlog(blogId, idToken) {
  const response = await axios.delete(
    BACKEND_URL + `blogs/${blogId}.json`,
    { params: authQueryParams(idToken) }
  );
  return response.data;
}

// ===== Assessments =====
// Public submit: POST to HTTPS function (Hosting rewrite at /api/submitAssessment)
function resolveSubmitUrl() {
  // Prefer explicit env override for non-hosted contexts
  const direct = process.env.REACT_APP_SUBMIT_URL || process.env.VITE_FUNCTIONS_URL;
  if (direct) return direct.replace(/\/$/, "");

  // In local dev, default to deployed HTTPS function unless emulators are explicitly enabled.
  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      const projectId = resolveProjectId();
      const region = resolveFunctionsRegion();
      return `http://127.0.0.1:5001/${projectId}/${region}/submitAssessment`;
    }
    return resolveDeployedFunctionUrl("submitAssessment");
  }

  // Default to same-origin API route when hosted behind Firebase Hosting rewrites
  return "/api/submitAssessment";
}

export async function storeAssessment(payload) {
  const url = resolveSubmitUrl();
  const body = { ...payload, createdAt: Date.now() };
  const res = await axios.post(url, body);
  // Function returns { id }
  return res.data;
}

// Read assessments securely (requires idToken). Returns array sorted by createdAt desc.
export async function getAssessments(idToken) {
  const response = await axios.get(BACKEND_URL + "assessments.json", {
    params: authQueryParams(idToken),
  });
  const items = [];
  const data = response.data || {};
  for (const [id, item] of Object.entries(data)) {
    items.push({ id, ...(item || {}) });
  }
  items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return items;
}

function resolveCampaignLeadSubmitUrl() {
  const direct = process.env.REACT_APP_SUBMIT_CAMPAIGN_LEAD_URL;
  if (direct) return direct.replace(/\/$/, "");

  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      const projectId = resolveProjectId();
      const region = resolveFunctionsRegion();
      return `http://127.0.0.1:5001/${projectId}/${region}/submitCampaignLead`;
    }
    return resolveDeployedFunctionUrl("submitCampaignLead");
  }

  return "/api/submitCampaignLead";
}

export async function storeCampaignLead(payload) {
  const url = resolveCampaignLeadSubmitUrl();
  const res = await axios.post(url, payload);
  return res.data;
}

function resolveCampaignLeadsUrl() {
  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      const projectId = resolveProjectId();
      const region = resolveFunctionsRegion();
      return `http://127.0.0.1:5001/${projectId}/${region}/getCampaignLeads`;
    }
    return resolveDeployedFunctionUrl("getCampaignLeads");
  }

  const direct = process.env.REACT_APP_GET_CAMPAIGN_LEADS_URL;
  if (direct) return direct.replace(/\/$/, "");

  return "/api/getCampaignLeads";
}

export async function getCampaignLeads(idToken, campaignId = "nyc-early-access") {
  const url = resolveCampaignLeadsUrl();
  const res = await axios.get(url, {
    params: { campaignId },
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
  });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return items;
}

function resolvePartnerIntakeSubmitUrl() {
  const direct = process.env.REACT_APP_SUBMIT_PARTNER_INTAKE_URL;
  if (direct) return direct.replace(/\/$/, "");

  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      const projectId = resolveProjectId();
      const region = resolveFunctionsRegion();
      return `http://127.0.0.1:5001/${projectId}/${region}/submitPartnerIntake`;
    }
    return resolveDeployedFunctionUrl("submitPartnerIntake");
  }

  return "/api/submitPartnerIntake";
}

function resolvePartnerCompletionEmailUrl() {
  const direct = process.env.REACT_APP_SEND_PARTNER_COMPLETION_EMAIL_URL;
  if (direct) return direct.replace(/\/$/, "");

  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      const projectId = resolveProjectId();
      const region = resolveFunctionsRegion();
      return `http://127.0.0.1:5001/${projectId}/${region}/sendPartnerIntakeCompletionEmail`;
    }
    return resolveDeployedFunctionUrl("sendPartnerIntakeCompletionEmail");
  }

  return "/api/sendPartnerIntakeCompletionEmail";
}

function resolveRestaurantOnboardingOutreachUrl() {
  const direct = process.env.REACT_APP_SEND_RO_OUTREACH_URL;
  if (direct) return direct.replace(/\/$/, "");

  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      const projectId = resolveProjectId();
      const region = resolveFunctionsRegion();
      return `http://127.0.0.1:5001/${projectId}/${region}/sendRestaurantOnboardingOutreachEmail`;
    }
    return resolveDeployedFunctionUrl("sendRestaurantOnboardingOutreachEmail");
  }

  return "/api/sendRestaurantOnboardingOutreachEmail";
}

function resolveGetRestaurantOnboardingOutreachUrl() {
  const direct = process.env.REACT_APP_GET_RO_OUTREACH_URL;
  if (direct) return direct.replace(/\/$/, "");

  if (isLocalhost()) {
    if (shouldUseFunctionsEmulator()) {
      const projectId = resolveProjectId();
      const region = resolveFunctionsRegion();
      return `http://127.0.0.1:5001/${projectId}/${region}/getRestaurantOnboardingOutreach`;
    }
    return resolveDeployedFunctionUrl("getRestaurantOnboardingOutreach");
  }

  return "/api/getRestaurantOnboardingOutreach";
}

export async function storePartnerIntake(payload) {
  const url = resolvePartnerIntakeSubmitUrl();
  const body = {
    ...payload,
    createdAt: Date.now(),
  };
  const res = await axios.post(url, body);
  return res.data;
}

export async function getPartnerIntakes(idToken, partnerId = "mass-restaurants-united") {
  const response = await axios.get(BACKEND_URL + "partnerIntakes.json", {
    params: authQueryParams(idToken),
  });

  const items = Object.entries(response.data || {})
    .map(([id, item]) => normalizePartnerIntakeRecord(id, item))
    .filter((item) => item.partnerId === partnerId);

  items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return items;
}

export async function sendPartnerIntakeCompletionEmail(idToken, { intakeId, instructions = "" }) {
  const url = resolvePartnerCompletionEmailUrl();
  const res = await axios.post(
    url,
    { intakeId, instructions },
    {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
    }
  );
  return res.data;
}

export async function sendRestaurantOnboardingOutreach(idToken, { ownerName, restaurantName, email, onboardingLink, calendlyLink, userStatus }) {
  const url = resolveRestaurantOnboardingOutreachUrl();
  const res = await axios.post(
    url,
    { ownerName, restaurantName, email, onboardingLink, calendlyLink, userStatus },
    {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
    }
  );
  return res.data;
}

export async function getRestaurantOnboardingOutreach(idToken) {
  const url = resolveGetRestaurantOnboardingOutreachUrl();
  const res = await axios.get(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
  });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  items.sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0));
  return items;
}

export async function getPlaybookLeads(idToken) {
  const url = resolvePlaybookLeadsUrl();
  const res = await axios.get(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
  });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return items;
}
